import { useState } from 'react'
import { getGraphqlErrorMessage } from './ai-chat-live-session'
import { getAiChatScopeVariables, isAiChatUnscoped } from './ai-chat-scope'
import type { AiChatScopeInput } from './ai-chat-scope'
import { useAiChatHistoryQuery } from '@/graphql/queries/aiChatHistoryQuery.gql.generated'

export function useAiChatHistory(input: AiChatScopeInput & { open: boolean }) {
  const query = useAiChatHistoryQuery({
    skip: !input.open || isAiChatUnscoped(input),
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: { ...getAiChatScopeVariables(input), offset: 0 },
  })
  const paging = useHistoryPaging(query)

  return {
    ...paging,
    loading: query.loading,
    errorMessage: paging.pageError ?? query.error?.message,
  }
}

function useHistoryPaging(query: ReturnType<typeof useAiChatHistoryQuery>) {
  const [loadingMore, setLoadingMore] = useState(false)
  const [pageError, setPageError] = useState<string>()
  const [exhausted, setExhausted] = useState(false)
  const sessions = query.data?.aiChatSessions ?? []
  const loadMore = async () => {
    if (loadingMore || query.loading) return
    setLoadingMore(true)
    setPageError(undefined)
    try {
      const page = await query.fetchMore({
        variables: { offset: sessions.length },
        updateQuery: (previous, { fetchMoreResult }) => ({
          aiChatSessions: [
            ...new Map(
              [
                ...previous.aiChatSessions,
                ...fetchMoreResult.aiChatSessions,
              ].map((session) => [session.id, session]),
            ).values(),
          ],
        }),
      })
      if (!page.data)
        throw new Error('Conversation history could not be loaded.')
      setExhausted(page.data.aiChatSessions.length < 30)
    } catch (error) {
      setPageError(getGraphqlErrorMessage(error))
    } finally {
      setLoadingMore(false)
    }
  }
  const retry = async () => {
    setPageError(undefined)
    setExhausted(false)
    try {
      await query.refetch()
    } catch (error) {
      setPageError(getGraphqlErrorMessage(error))
    }
  }
  const reset = () => {
    setExhausted(false)
    setPageError(undefined)
  }

  return {
    sessions,
    loadingMore,
    pageError,
    canLoadMore:
      !exhausted && sessions.length > 0 && sessions.length % 30 === 0,
    loadMore,
    retry,
    reset,
  }
}
